//-- Copyright (c) 2021 TMT Insights Group, LLC
import { selectCurrentFolderConnectors, selectCurrentPath } from '@/redux/selectors/fileManagerSelectors';
import { selectFileQueue, selectFileQueueCount } from '@/redux/selectors/fileQueueSelectors';
import { addMultiToFileQueue, addToFileQueue, clearFileQueue, moveOrCopyFiles, removeFromFileQueue } from '@/redux/slices/fileQueueSlice';
import styled from '@emotion/styled';
import { Button, IconButton, List, ListItem, Popover, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { refreshFiles } from '@/redux/slices/fileManagerSlice';
import CopyIcon from '@/components/_common/Icons/CopyIcon';
import MoveIcon from '@/components/_common/Icons/MoveIcon';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useToast } from '@/components/_common/Toast';
import { FileManagerInventoryFile } from '@/redux/slices/slice_types';
import { selectCanWriteFileManager } from '@/redux/selectors/authSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import FileItemText from '../FilesContainer/FileItem/FileItemText';

const FileQueue = () => {
  const dispatch = useAppDispatch();
  const canWriteFileManager = useAppSelector(selectCanWriteFileManager);
  const fileQueueItems = useAppSelector(selectFileQueue);
  const count = useAppSelector(selectFileQueueCount);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [fileQueueOpen, setFileQueueOpen] = useState(false);
  const { info, success, closeSnackbar, error } = useToast();
  const path = useAppSelector(selectCurrentPath);
  const targetFolder = path[path.length - 1];
  const connectors = useAppSelector(selectCurrentFolderConnectors);
  const isReadOnly = connectors.is_read_only;

  if (!canWriteFileManager) return null;

  useEffect(() => {
    if (count === 0) onFileQueueClose();
  }, [count]);

  const onFileQueueOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setFileQueueOpen(true);
  };

  const onFileQueueClose = () => {
    setAnchorEl(null);
    setFileQueueOpen(false);
  };

  const removeFileQueueItem = (item: FileManagerInventoryFile) => {
    const undo = () => {
      dispatch(addToFileQueue(item));
      closeSnackbar(toast);
    };
    dispatch(removeFromFileQueue(item));
    const toast: any = info('Removed file from File Queue.', {
      action: <Button onClick={undo}>Undo</Button>
    });
  };

  const clearAll = () => {
    const items = [...fileQueueItems];
    const undo = () => {
      dispatch(addMultiToFileQueue(items));
      closeSnackbar(toast);
    };
    dispatch(clearFileQueue());
    const toast: any = info('Removed all files from File Queue.', {
      action: <Button onClick={undo}>Undo</Button>
    });
  };

  const handleCopyFiles = () => {
    // @ts-ignore
    (dispatch(moveOrCopyFiles('copy')) as any)
      .unwrap()
      .then(({ items }: { items: FileManagerInventoryFile[] }) => {
        const itemCount = items.length;
        success(`Copied item${itemCount > 1 ? 's' : ''} to ${targetFolder}`);
        setTimeout(() => dispatch(refreshFiles()), 250);
      })
      .catch(() => {
        error(`There was an error copying the file(s)`);
        setTimeout(() => dispatch(refreshFiles()), 250);
      });
  };

  const handleMoveFiles = () => {
    // @ts-ignore
    (dispatch(moveOrCopyFiles('move')) as any)
      .unwrap()
      .then((res: any) => {
        const itemCount = res.items.length;
        success(`Moved item${itemCount > 1 ? 's' : ''} to ${targetFolder}`);
        setTimeout(() => dispatch(refreshFiles()), 250);
      })
      .catch(() => {
        error(`There was an error moving the file(s)`);
        setTimeout(() => dispatch(refreshFiles()), 250);
      });
  };

  const createTooltip = (action: string) => {
    if (isReadOnly) {
      return 'This bucket is read-only';
    } else {
      return `${action.charAt(0).toUpperCase() + action.slice(1)} to folder`;
    }
  };

  return (
    <FileQueueActions>
      <Button
        data-testid="file-queue-btn"
        aria-describedby="fileQueue-popover"
        variant="outlined"
        color="primary"
        onClick={onFileQueueOpen}
        startIcon={<PolarisIcon icon="assignment" />}
        disabled={count < 1 || isReadOnly}
        sx={{ mr: 1.5 }}
      >
        File Queue
        {!!count && <Count>{count}</Count>}
      </Button>

      <Popover
        id="fileQueue-popover"
        open={fileQueueOpen}
        anchorEl={anchorEl}
        onClose={onFileQueueClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ marginTop: 5 }}
      >
        <FileQueuePopoverList data-testid="file-queue-list">
          {fileQueueItems.map((item: FileManagerInventoryFile) => (
            <FileQueueItem key={item.key}>
              <FileItemText name={item.name} />
              <IconButton onClick={() => removeFileQueueItem(item)} size="large">
                <CloseButton icon="close" height={18} />
              </IconButton>
            </FileQueueItem>
          ))}
        </FileQueuePopoverList>
        <ClearAllButton fullWidth onClick={clearAll}>
          clear all
        </ClearAllButton>
      </Popover>

      <Tooltip title={createTooltip('copy')}>
        <span>
          <StyledIconButton data-testid="copy-files-btn" color="secondary" onClick={handleCopyFiles} disabled={count < 1 || isReadOnly} size="large">
            <CopyIcon />
          </StyledIconButton>
        </span>
      </Tooltip>
      <Tooltip title={createTooltip('move')}>
        <span>
          <StyledIconButton
            data-testid="move-files-btn"
            sx={{ mr: 0 }}
            color="secondary"
            onClick={handleMoveFiles}
            disabled={count < 1 || isReadOnly}
            size="large"
          >
            <MoveIcon />
          </StyledIconButton>
        </span>
      </Tooltip>
    </FileQueueActions>
  );
};

export default FileQueue;

const FileQueueActions = styled.div`
  padding: ${({ theme }) => theme.spacing(1.5, 2)};
  margin-left: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 10px;
  & button {
    &.Mui-disabled {
      border-color: rgba(0, 0, 0, 0.26);
    }
  }
`;

const FileQueuePopoverList = styled(List)`
  width: 400px;
  max-height: 70vh;
  overflow: auto;
  padding-bottom: ${({ theme }) => theme.spacing(5.5)};
`;

const FileQueueItem = styled(ListItem)`
  justify-content: space-between;
  & > div {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledIconButton = styled(IconButton)`
  border: 1px solid ${({ theme }) => theme.palette.primary.light};
  color: ${({ theme }) => theme.palette.primary.light};
  padding: ${({ theme }) => theme.spacing(1)};
  margin-right: ${({ theme }) => theme.spacing(1.5)};
  height: 40px;
  width: 40px;
`;

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.palette.primary.light};
  border-radius: 50%;
  width: 22px;
  height: 22px;
  font-size: 0.8rem;
  color: white;
  margin-left: ${({ theme }) => theme.spacing(1)};
  margin-right: ${({ theme }) => theme.spacing(-1)};
  line-height: 0.8rem;
  padding-left: ${({ theme }) => theme.spacing(0.25)};
`;

const CloseButton = styled(PolarisIcon)`
  &.MuiIconButton-root {
    padding: ${({ theme }) => theme.spacing(0.25)};
    margin-left: ${({ theme }) => theme.spacing(2)};

    &:hover {
      color: ${({ theme }) => theme.palette.error.main};
      background: ${({ theme }) => theme.palette.error.main + '2a'};
    }
  }
`;

const ClearAllButton = styled(Button)`
  border-radius: 0;
  background: ${({ theme }) => theme.palette.background.paper};
  position: absolute;
  bottom: 0;
  color: ${({ theme }) => theme.palette.error.light};
  z-index: 9;
  &:hover {
    color: ${({ theme }) => theme.palette.error.dark};
    background: ${({ theme }) => theme.palette.background.default};
  }
`;
